import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Text,
  useToast,
  Heading,
  useColorModeValue,
  Button,
  FormControl,
  FormLabel,
  Input,
  Divider,
  Flex,
  Link,
} from "@chakra-ui/react";
import { useNavigate, Navigate, Link as RouterLink } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { DynamicWidget } from "@dynamic-labs/sdk-react-core";
import { useFeatureFlags } from "../../context/featureFlagsContext";

import DynamicLoginButton from "../../components/DynamicLoginButton";

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, refetchUser, primaryWallet } = useUser();
  const { featureFlags } = useFeatureFlags();
  const showTraditionalLogin = featureFlags?.SHOW_TRADITIONAL_LOGIN;

  const toast = useToast();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("white", "gray.800");
  const dividerBg = useColorModeValue("gray.200", "gray.600");

  // Navigate to projects page when wallet is connected
  useEffect(() => {
    if (primaryWallet) {
      toast({
        title: "Wallet Connected",
        description: "You have successfully connected your wallet.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/my-projects");
    }
  }, [primaryWallet, navigate, toast]);

  if (user) {
    return <Navigate to="/" />;
  }

  const handleLogIn = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/login`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Invalid login credentials");
      }

      toast({
        title: "Login Successful",
        description: "You have successfully logged in.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      refetchUser();
      navigate("/my-projects");
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Login Failed",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      width="95%"
      maxW="500px"
      mx="auto"
      my="auto"
      p="10"
      borderWidth="1px"
      borderRadius="lg"
      bg={bgColor}
      boxShadow="lg"
    >
      <Heading fontSize="2xl" textAlign="center" mb="6" as="h2">
        Sign In
      </Heading>

      <VStack spacing="6">
        <Text textAlign="center" color="gray.600">
          Sign in with your wallet to access your projects and manage your
          account
        </Text>

        {/* Custom wallet sign in button using DynamicWidget's innerButtonComponent */}
        <Box mb={showTraditionalLogin ? 0 : 6}>
          <DynamicWidget innerButtonComponent={<DynamicLoginButton />} />
        </Box>
        <Flex width="100%" align="center">
          <Divider flex="1" bg={dividerBg} />
          <Text px="3" color="gray.500" fontSize="sm">
            OR
          </Text>
          <Divider flex="1" bg={dividerBg} />
        </Flex>

        <form onSubmit={handleLogIn} style={{ width: "100%" }}>
          <VStack spacing="5">
            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormControl>
            <Button
              width="full"
              type="submit"
              isLoading={loading}
              variant="primary"
            >
              Log In
            </Button>
            <Text fontSize="sm" color="gray.600" textAlign="center">
              Don&apos;t have an account?{" "}
              <Link
                as={RouterLink}
                to="/signup"
                color="primary"
                fontWeight="medium"
              >
                Create one
              </Link>
            </Text>
          </VStack>
        </form>
      </VStack>
    </Box>
  );
};

export default SignIn;
